/**
 * SlickPay is inconsistent about whether a payload is returned bare or wrapped
 * in `{ data: ... }` — it varies by endpoint and, in sandbox, by response.
 * Every read goes through here so callers never have to write `x.id ?? x.data?.id`.
 */
export function unwrap(body) {
  if (body && typeof body === "object" && !Array.isArray(body) && "data" in body) {
    const inner = body.data;
    if (inner && typeof inner === "object") return inner;
  }
  return body;
}

/** Reads `key` from a payload whether or not it is wrapped in `{ data }`. */
export function pick(body, ...keys) {
  const sources = [body, body?.data].filter((s) => s && typeof s === "object");
  for (const key of keys) {
    for (const source of sources) {
      if (source[key] !== undefined && source[key] !== null) return source[key];
    }
  }
  return undefined;
}

/**
 * Whether an invoice payload represents a completed payment.
 *
 * The paid/unpaid signal is a numeric flag, not a status string: `completed`
 * at the top level, `pay_status` when nested. Both `1` and `true` occur.
 */
export function isPaid(invoice) {
  const flag = pick(invoice, "completed", "pay_status");
  return flag === 1 || flag === true || flag === "1";
}

/**
 * SlickPay's contact endpoint requires a `rib` (20 numeric digits, unique per
 * contact) even when the payer is just settling a one-off fee and has no
 * reason to hand over a real bank account number. Generated per contact.
 */
export function generateRib() {
  let rib = "";
  for (let i = 0; i < 20; i++) rib += Math.floor(Math.random() * 10);
  return rib;
}

/** Drops undefined/null entries so they are never sent as literal nulls. */
export function compact(object) {
  return Object.fromEntries(
    Object.entries(object).filter(([, value]) => value !== undefined && value !== null)
  );
}
